import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import type { AdsManagerAction, ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { getProductCategory } from 'bundles/enroll-modal/utils/gtmUtils';
import type { CourseType } from 'bundles/enterprise-legacy-learner-home/utils/courseTypeMetadataUtils';
import adsTracker from 'bundles/event-tracking/lib';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';

export type UseEnrollmentTrackingProps = {
  courseId: string;
  s12nId?: string;
  name?: string;
  isSpecialization?: boolean;
  courseType?: CourseType;
  s12nProductVariant?: DescriptionPageProductVariant | S12nProductVariant;
  router: InjectedRouter;
};

export type UseEnrollmentTrackingResult = {
  trackEnroll: () => void;
  trackEnrollButtonClick: () => void;
  getProductType: () => ProductType | undefined;
};

/**
 * Fires enrollment GTM/ads tracking for the enroll modal. Wraps the eventing v3
 * tracker and the legacy `adsTracker` so the orchestrator stays decoupled from
 * the tracking plumbing. `getProductType` is exposed because the experiments and
 * routing concerns also derive the product type.
 */
export const useEnrollmentTracking = ({
  courseId,
  s12nId,
  name,
  isSpecialization,
  courseType,
  s12nProductVariant,
  router,
}: UseEnrollmentTrackingProps): UseEnrollmentTrackingResult => {
  const track = useTracker();

  const getGTMTrackingData = () => {
    const productCategory = getProductCategory({
      courseType,
      s12nProductVariant: isSpecialization ? s12nProductVariant : undefined,
    });
    const data = {
      userId: user.get().id,
      productCategory,
      productName: name,
      productSlug: router.params?.slug || router.params?.productSlug,
      productId: isSpecialization ? s12nId : courseId,
    };
    return data;
  };

  const getProductType = () => {
    let productType: ProductType | undefined;

    if (isSpecialization) {
      productType = 's12n';
    } else if (courseType === 'GuidedProject') {
      productType = 'guided_project';
    } else if (courseType === 'Project') {
      productType = 'project';
    } else {
      productType = 'course';
    }

    return productType;
  };

  const trackGTMForEventingV3 = (adsManagerAction: AdsManagerAction, productId?: string) => {
    if (!productId) {
      return;
    }

    track('ads_tracker_google_tag_manager', {
      adsManagerAction,
      product: {
        id: productId,
        type: getProductType(),
      },
    });
  };

  const trackEnroll = () => {
    // Introduce timeout so that impressions can fire before the tracking
    setTimeout(() => {
      const data = getGTMTrackingData();

      trackGTMForEventingV3('enroll', data.productId);
      adsTracker.trackEnroll(data);
    }, 100);
  };

  const trackEnrollButtonClick = () => {
    // Introduce timeout so that impressions can fire before the tracking
    setTimeout(() => {
      const data = getGTMTrackingData();

      trackGTMForEventingV3('enroll_button_click', data.productId);
      adsTracker.trackEnrollButtonClick(data);
    }, 100);
  };

  return {
    trackEnroll,
    trackEnrollButtonClick,
    getProductType,
  };
};

export default useEnrollmentTracking;
